/**
 * GeneratePresentation.tsx
 *
 * Unified 11-step presentation wizard.
 *
 * Steps 1-5 (Setup):  Ciudad → Plantilla → Tema → Proveedores → Detalles
 * Steps 6-11 (Build): Preguntas → Planificación → Diapositivas → Imágenes → Costos → Generar
 *
 * The DashboardLayout header shows the WizardTimeline (replaces "Ebombo" text).
 * Navigation is via the timeline — clicking completed steps goes back.
 * No per-step back buttons; the timeline IS the navigation.
 *
 * Transition from setup→build fires plan generation in parallel so the
 * Planificación step sees skeletons immediately and the plan arrives ~5-15s later.
 * The QuestionsStep (build step 1) mounts first and generates questions on mount.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';

import type { WizardFormData, ProviderSummary } from '@/types/wizard';
import {
  SelectCity,
  SelectTemplate,
  SelectProviders,
  EventDetails,
} from './steps';
import { SelectThemeStep } from '@/components/themes';
import { WizardTimeline } from './components';
import {
  QuestionsStep,
  PlanificationStep,
  SlideConfigStep,
  ImageGenerationStep,
  CostsStep,
  FinalizeStep,
} from './phases';
import { useWizardStore } from '@/stores/useWizardStore';
import { useCreateDraft, useUpdateDraft } from '@/api/useWizardV2';
import styles from './GeneratePresentation.module.css';

// ─── Debounce helper ────���────────────────────────────────────────────────────

function useDebouncedCallback<T extends unknown[]>(
  fn: (...args: T) => void,
  delay: number,
): ((...args: T) => void) & { flush: () => void } {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fnRef = useRef(fn);
  const pendingArgs = useRef<T | null>(null);
  fnRef.current = fn;

  const debounced = useCallback(
    (...args: T) => {
      pendingArgs.current = args;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        pendingArgs.current = null;
        fnRef.current(...args);
      }, delay);
    },
    [delay],
  ) as ((...args: T) => void) & { flush: () => void };

  debounced.flush = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (pendingArgs.current) {
      const args = pendingArgs.current;
      pendingArgs.current = null;
      fnRef.current(...args);
    }
  };

  return debounced;
}

// ─── Layout context type ──���──────────────────────────────────────────────────

type LayoutContext = {
  setHeaderSlot: (node: React.ReactNode) => void;
  setHeaderTitle: (node: React.ReactNode) => void;
};

// ─── Component ───────────────���───────────────────────────���───────────────────

export const GeneratePresentation = () => {
  const {
    activeBar,
    setupStep,
    buildStep,
    highestSetupStep,
    highestBuildStep,
    formData,
    selectedProviders,
    presentationId,
    goToSetupStep,
    goToBuildStep,
    transitionToBar2,
    finishTransition,
    updateFormData,
    setSelectedProviders,
    setPresentationId,
  } = useWizardStore();

  const { setHeaderTitle } = useOutletContext<LayoutContext>();

  // ── API mutations ─────────────────────────────────────────────────────────
  const createDraft = useCreateDraft();
  const updateDraft = useUpdateDraft();

  // ── Save wizard build step to generation_progress ──────────────────────────
  const BUILD_STEP_PHASES = ['questions', 'plan', 'generate_deck', 'images', 'costs', 'build_pptx'] as const;
  useEffect(() => {
    if (!presentationId || activeBar !== 'build') return;
    updateDraft.mutate({
      id: presentationId,
      data: {
        generation_progress: {
          phase: BUILD_STEP_PHASES[buildStep - 1],
          wizard_step: buildStep,
          total_images: 0,
          completed_images: 0,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildStep, activeBar, presentationId]);

  // ── Debounced auto-save ────────────────────────────────────────────────────
  const debouncedSave = useDebouncedCallback(
    (id: string, data: Record<string, unknown>) => {
      updateDraft.mutate({ id, data });
    },
    500,
  );

  // ── Timeline step click handler ─────────────���──────────────────────────────
  const handleTimelineClick = useCallback(
    (bar: 'setup' | 'build', step: number) => {
      if (bar === 'setup') {
        goToSetupStep(step as 1 | 2 | 3 | 4 | 5);
      } else {
        goToBuildStep(step as 1 | 2 | 3 | 4 | 5 | 6);
      }
    },
    [goToSetupStep, goToBuildStep],
  );

  // ── Inject timeline into DashboardLayout header ────────────────────────────
  useEffect(() => {
    setHeaderTitle(
      <WizardTimeline
        activeBar={activeBar}
        setupStep={setupStep}
        buildStep={buildStep}
        highestSetupStep={highestSetupStep}
        highestBuildStep={highestBuildStep}
        onStepClick={handleTimelineClick}
      />,
    );
    return () => setHeaderTitle(null);
  }, [activeBar, setupStep, buildStep, highestSetupStep, highestBuildStep, handleTimelineClick, setHeaderTitle]);

  // ── Transition to build bar ────────────────────────────────────────────────
  const handleTransitionToBuild = useCallback(() => {
    // Flush any pending debounced save so field edits aren't lost.
    debouncedSave.flush();

    // Read form data directly from the store (not from the closure) to
    // guarantee we capture the latest keystrokes.
    const latestForm = useWizardStore.getState().formData;
    const pid = useWizardStore.getState().presentationId;

    if (pid) {
      updateDraft.mutate({
        id: pid,
        data: {
          event_name: latestForm.event_name,
          client_name: latestForm.client_name,
          prompt: latestForm.prompt,
          logo_url: latestForm.logo_url,
          event_date: latestForm.event_date || null,
          attendees: latestForm.attendees,
        },
      });
    }

    transitionToBar2();
    finishTransition();
  }, [transitionToBar2, finishTransition, debouncedSave, updateDraft]);

  // ── Form helpers ───────────────────────────────────────────────────────────
  const updateForm = useCallback(
    <K extends keyof WizardFormData>(key: K, value: WizardFormData[K]) => {
      updateFormData({ [key]: value } as Partial<WizardFormData>);
      if (presentationId) {
        debouncedSave(presentationId, { [key]: value } as Partial<WizardFormData>);
      }
    },
    [updateFormData, presentationId, debouncedSave],
  );

  const handleProvidersChange = useCallback(
    (providers: ProviderSummary[]) => {
      setSelectedProviders(providers);
      if (presentationId) {
        const ids = providers.map((p) => p.id);
        debouncedSave(presentationId, {
          context_data: { provider_ids: ids },
        });
      }
    },
    [setSelectedProviders, presentationId, debouncedSave],
  );

  const handleCitySelect = useCallback(
    (city: string) => {
      updateFormData({ city });
      createDraft.mutate(
        { ...formData, city },
        {
          onSuccess: (draft) => {
            setPresentationId(draft.uuid);
          },
        },
      );
      goToSetupStep(2);
    },
    [updateFormData, createDraft, formData, setPresentationId, goToSetupStep],
  );

  // ── Step content ───────────────────────────────────────────────────────────
  const renderContent = () => {
    if (activeBar === 'setup') {
      switch (setupStep) {
        case 1:
          return <SelectCity selected={formData.city} onSelect={handleCitySelect} />;
        case 2:
          return (
            <SelectTemplate
              selected={formData.plantilla_slug}
              onSelect={(slug) => {
                updateForm('plantilla_slug', slug);
                goToSetupStep(3);
              }}
            />
          );
        case 3:
          return (
            <SelectThemeStep
              selected={formData.theme_slugs}
              onConfirm={(slugs) => {
                updateForm('theme_slugs', slugs);
                goToSetupStep(4);
              }}
              title="Select Event Themes"
              subtitle="Choose one or more themes — the first selected defines the primary style"
              templateSlug={formData.plantilla_slug}
            />
          );
        case 4:
          return (
            <SelectProviders
              selectedProviders={selectedProviders}
              onProvidersChange={handleProvidersChange}
              onNext={() => goToSetupStep(5)}
              city={formData.city}
            />
          );
        case 5:
          return (
            <EventDetails
              formData={formData}
              updateForm={updateForm}
              onGenerate={handleTransitionToBuild}
            />
          );
        default:
          return null;
      }
    }

    switch (buildStep) {
      case 1:
        return <QuestionsStep />;
      case 2:
        return <PlanificationStep />;
      case 3:
        return <SlideConfigStep />;
      case 4:
        return <ImageGenerationStep />;
      case 5:
        return <CostsStep />;
      case 6:
        return <FinalizeStep />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.wizard}>
      <div className={styles.stepContent}>
        {renderContent()}
      </div>
    </div>
  );
};
